import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Spin } from 'antd';
import {
	CheckOutlined,
	ClockCircleOutlined,
	HourglassOutlined,
	InfoCircleOutlined,
	MinusSquareOutlined,
	PlusSquareOutlined,
} from '@ant-design/icons';
import { fetchComplianceRequestInstancesList } from '../../thunks/compliance_request_instances_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { COMPLIANCE_INSTANCE_STATUSES, ComplianceDocument } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Purple/7 from the design — the "+N more" reveal.
const MORE_COLOR = '#54298E';
// Client chips shown before "+N more".
const MAX_CHIPS = 9;

// Document/supplier perspective labels (the supplier owns the document and sees
// each client's approval state of it).
const STATUS_LINES = [
	{
		key: COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER,
		label: 'Waiting on You',
		icon: <HourglassOutlined style={{ color: 'rgb(219, 123, 43)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW,
		label: 'Pending Approval',
		icon: <ClockCircleOutlined style={{ color: 'rgb(171, 133, 15)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.APPROVED,
		label: 'Approved',
		icon: <CheckOutlined style={{ color: 'rgb(27, 121, 34)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.REJECTED,
		label: 'Rejected',
		icon: <InfoCircleOutlined style={{ color: 'rgb(204, 50, 50)' }} />,
	},
];

interface StatusLineProps {
	documentId: number;
	statusKey: string;
	label: string;
	icon: React.ReactNode;
	count: number;
	fetchInstances: (params: any) => Promise<any[]>;
}

// One status line: "[+] <icon> <label>: <count>". The [+] expands the clients in
// that status inline (lazy-fetched) as grey chips, capped with "+N more". There is
// no client detail route from the supplier side, so the client name renders as a
// plain grey chip (no link).
const StatusLine: FC<StatusLineProps> = ({
	documentId,
	statusKey,
	label,
	icon,
	count,
	fetchInstances,
}): React.ReactElement => {
	const [expanded, setExpanded] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [loading, setLoading] = useState(false);
	// null = not yet fetched
	const [clients, setClients] = useState(null);

	const toggle = () => {
		const next = !expanded;
		setExpanded(next);
		if (next && clients === null) {
			setLoading(true);
			fetchInstances({ linkedComplianceDocumentId: documentId, statuses: statusKey }).then(
				(instances) => {
					setClients(
						(instances || []).map((instance) => ({
							id: nullSafeGet('buyerCompanyId', instance),
							name: nullSafeGetOrElse('buyerCompanyName', instance, '--'),
						}))
					);
					setLoading(false);
				},
				() => {
					setClients([]);
					setLoading(false);
				}
			);
		}
	};

	const allClients = clients || [];
	const shownClients = showAll ? allClients : allClients.slice(0, MAX_CHIPS);
	const remaining = allClients.length - shownClients.length;

	return (
		<div className="flex flex-col gap-1">
			<div className="bodyregular flex flex-row items-center gap-1 text-character-primary">
				<span
					role="button"
					onClick={toggle}
					className="flex cursor-pointer items-center text-grey-7"
				>
					{expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
				</span>
				{icon}
				<span>
					{label}: {count}
				</span>
			</div>
			{expanded ? (
				loading ? (
					<div className="pl-5 pt-1">
						<Spin size="small" />
					</div>
				) : (
					<div className="flex flex-row flex-wrap items-center gap-2 pl-5">
						{shownClients.map((client, index) => (
							<div
								key={index}
								className="footnoteregular inline-flex items-center gap-1 rounded-sm border border-solid border-grey-5 bg-grey-2 px-2 text-character-secondary"
							>
								{client.name}
							</div>
						))}
						{remaining > 0 ? (
							<span
								role="button"
								onClick={() => setShowAll(true)}
								className="bodyregular cursor-pointer"
								style={{ color: MORE_COLOR }}
							>
								<span className="bodybold">+{remaining}</span> more
							</span>
						) : null}
					</div>
				)
			) : null}
		</div>
	);
};

interface DocumentClientApprovalStatusCountsProps {
	complianceDocument: ComplianceDocument;
	fetchInstances: (params: any) => Promise<any[]>;
}

// Per-document client approval status: one expandable line per non-zero status,
// counts read straight off the document's hydrated `clientStatusCounts` (no fetch
// for the counts); expanding a status lazy-fetches that status's client list.
const DocumentClientApprovalStatusCounts: FC<DocumentClientApprovalStatusCountsProps> = ({
	complianceDocument,
	fetchInstances,
}): React.ReactElement => {
	const counts = nullSafeGetOrElse('clientStatusCounts', complianceDocument, {});
	const lines = STATUS_LINES.filter((line) => nullSafeGet(line.key, counts));
	return (
		<div className="flex flex-col gap-2">
			{lines.length > 0 ? (
				lines.map((line) => (
					<StatusLine
						key={line.key}
						documentId={complianceDocument.id}
						statusKey={line.key}
						label={line.label}
						icon={line.icon}
						count={counts[line.key]}
						fetchInstances={fetchInstances}
					/>
				))
			) : (
				<span className="text-grey-8">No clients</span>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	fetchInstances: (params) =>
		dispatch(fetchComplianceRequestInstancesList(ROLE_TYPES.SUPPLIER)(params)),
});

export default connect(null, mapDispatchToProps)(DocumentClientApprovalStatusCounts);
